import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { StoreService } from '../../store/store.service';
import { environment } from '../../../../environments/environment';
import { catchError, map, of, Subject, takeUntil, tap } from 'rxjs';
import {
  ShippingTemplateInterface,
  ShippingTemplateListResponseInterface,
  ShippingTemplateResponseInterface,
} from '../../interfaces/shipping-templates/shipping-template-interface';
import {
  SearchV2RequestInterface,
  SearchV2ResponseInterface,
} from '@component-library';
import { GlobalMessageService } from '../global-services/global-message-service/global-message.service';
import { BroadcastChannelService } from '../../broadcast-channel/services/broadcast-channel.service';

@Injectable({
  providedIn: 'root',
})
export class ShippingTemplatesService implements OnDestroy {
  globalMessageService = inject(GlobalMessageService);
  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private storeService: StoreService,
    private broadcastChannelService: BroadcastChannelService
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  searchv2(searchv2: SearchV2RequestInterface) {
    return this.http
      .post<SearchV2ResponseInterface>(
        `${environment.baseUrl}/shippingTemplates/searchV2`,
        searchv2
      )
      .pipe(
        catchError(() =>
          of({
            CurrentPage: 0,
            CurrentPageSize: 0,
            Items: [],
            Message: '',
            Status: false,
            TotalCount: 0,
          })
        ),
        map((response: SearchV2ResponseInterface) => {
          if (response && response.Status) {
            return {
              CurrentPage: response.CurrentPage || 0,
              CurrentPageSize: response.CurrentPageSize || 0,
              Items: response.Items || [],
              Message: response.Message || '',
              Status: response.Status || false,
              TotalCount: response.TotalCount || 0,
            };
          }
          return {
            CurrentPage: 0,
            CurrentPageSize: 0,
            Items: [],
            Message: '',
            Status: false,
            TotalCount: 0,
          };
        })
      );
  }

  getAllShippingTemplateRequest = (sendMessage: boolean) => {
    return this.http
      .get<ShippingTemplateListResponseInterface>(
        `${environment.baseUrl}/shippingTemplates/list?GetAll=true&Page=0&PageSize=999`
      )
      .pipe(
        map((res) => {
          if (res.Status) {
            this.storeService.shippingTemplate$.set(res.Items);
            if (sendMessage) {
              this.broadcastChannelService.shippingTemplateTableChannelSend(
                res.Items
              );
            }

            return res.Items;
          }
          throw res.Message;
        }),
        catchError((error) => {
          return of({
            Items: [],
            Status: false,
            Message: error,
            TotalCount: 0,
            Page: 0,
            PageSize: 0,
            Exception: '',
          });
        })
      );
  };

  refreshStore = () => {
    const subscription = this.getAllShippingTemplateRequest(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        subscription.unsubscribe();
      });
  };

  addShippingTemplate(shippingTemplate: ShippingTemplateInterface) {
    return this.http
      .post<ShippingTemplateResponseInterface>(
        `${environment.baseUrl}/shippingTemplates/add`,
        shippingTemplate
      )
      .pipe(
        catchError(() =>
          of({
            ShippingTemplate: {} as ShippingTemplateInterface,
            Status: false,
            Message:
              'Kargo şablonu ekleme sırasında bir hata oluştu lütfen tekrar deneyiz',
          })
        ),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f && f?.Status,
            summary: 'Kargo Şablonu Ekle',
            infoDetail: 'Kargo şablonu ekleme işlemi başarıyla sonuçlandı.',
            errorDetail: 'Kargo şablonu ekleme işlemi başarısız oldu.',
          });
          if (f && f?.Status) {
            this.refreshStore();
          }
        })
      );
  }

  removeShippingTemplate(shippingTemplate: ShippingTemplateInterface) {
    return this.http
      .post<{ Status: boolean; Message: string }>(
        `${environment.baseUrl}/shippingTemplates/delete`,
        shippingTemplate
      )
      .pipe(
        catchError(() =>
          of({
            Status: false,
            Message:
              'Kargo şablonu ekleme işlemi sırasında bir hata oluştu lütfen tekrar deneyiz',
          })
        ),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f && f.Status,
            summary: 'Kargo Şablonu Sil',
            infoDetail: 'Kargo Şablonu Başarıyla Silindi',
            errorDetail: f.Message,
          });
          if (f) {
            this.refreshStore();
          }
        })
      );
  }

  updateShippingTemplate(shippingTemplate: ShippingTemplateInterface) {
    return this.http
      .post<ShippingTemplateResponseInterface>(
        `${environment.baseUrl}/shippingTemplates/update`,
        shippingTemplate
      )
      .pipe(
        catchError(() =>
          of({
            ShippingTemplate: {} as ShippingTemplateInterface,
            Status: false,
            Message:
              'Kargo şablonu güncelleme işlemi sırasında bir hata oluştu lütfen tekrar deneyiz',
          })
        ),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f && f?.Status,
            summary: 'Kargo Şablonu Güncelle',
            infoDetail: 'Kargo şablonu güncelleme işlemi başarıyla sonuçlandı.',
            errorDetail: 'Kargo şablonu güncelleme işlemi başarısız oldu.',
          });
          if (f && f?.Status) {
            this.refreshStore();
          }
        })
      );
  }
}
